import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, downloadUrl } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { ArrowLeft, Download, FileText, RefreshCw, Tag, Eye } from "lucide-react";

const NIVEAU_LABELS = {
  PUBLIC: "Public", ADHERENT: "Adhérents", PRATICIEN: "Praticiens",
  SEMINAIRE_SPECIFIQUE: "Événement", ADMIN: "Admin",
};

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export default function MemberDocumentDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(false);

  const load = useCallback(() => {
    setError(null);
    api(`/membre/documents/${id}`).then(setDoc).catch((e) => setError(e.message));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const signedUrl = doc?.url_telechargement ? downloadUrl(doc.url_telechargement) : null;
  const downloadable = doc?.telechargeable !== false;
  const isPdf = doc?.extension === "pdf";

  // Fetch the PDF as a blob so a missing/expired file shows a clean message
  // instead of the raw {"detail":"Not Found"} JSON inside the iframe.
  useEffect(() => {
    if (!isPdf || !signedUrl) return;
    let revoked = null;
    setPreviewError(false);
    setPreviewUrl(null);
    fetch(signedUrl, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error("indispo"); return r.blob(); })
      .then((blob) => { revoked = URL.createObjectURL(blob); setPreviewUrl(revoked); })
      .catch(() => setPreviewError(true));
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [isPdf, signedUrl]);

  return (
    <AppLayout>
      <div data-testid="document-detail-page" className="max-w-4xl mx-auto space-y-6 animate-fade-in">
        <Link to="/documents" data-testid="doc-back-link"
          className="inline-flex items-center gap-1.5 text-sm text-cfhtb-secondary hover:text-cfhtb-primary transition-colors">
          <ArrowLeft className="h-4 w-4" /> Retour à la base documentaire
        </Link>

        {error ? (
          <div className="bg-white rounded-lg border border-cfhtb-error/30 p-6 text-sm text-cfhtb-error" data-testid="doc-error">{error}</div>
        ) : !doc ? (
          <div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
        ) : (
          <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8 border-b border-slate-100">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-md bg-cfhtb-bg flex items-center justify-center shrink-0">
                    <FileText className="h-6 w-6 text-cfhtb-primary" />
                  </div>
                  <div>
                    <h1 className="font-serif text-2xl sm:text-3xl font-bold text-cfhtb-heading tracking-tight" data-testid="doc-title">
                      {doc.titre}
                    </h1>
                    <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-cfhtb-text">
                      <span className="uppercase font-semibold text-cfhtb-secondary">{doc.extension}</span>
                      <span>{formatSize(doc.taille)}</span>
                      <span>Version {doc.version}</span>
                      {doc.categorie_nom && <span className="bg-cfhtb-bg px-2 py-0.5 rounded-full">{doc.categorie_nom}</span>}
                      <span className="bg-cfhtb-primary/10 text-cfhtb-primary px-2 py-0.5 rounded-full font-medium">
                        {NIVEAU_LABELS[doc.niveau_acces] || doc.niveau_acces}
                      </span>
                    </div>
                  </div>
                </div>
                {downloadable ? (
                  <a
                    href={signedUrl}
                    download={doc.nom_fichier}
                    data-testid="doc-download-button"
                    className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-4 py-2.5 text-sm font-medium hover:bg-[#1f1975] transition-colors"
                  >
                    <Download className="h-4 w-4" /> Télécharger
                  </a>
                ) : (
                  <span data-testid="doc-consultation-only"
                    className="inline-flex items-center gap-2 bg-cfhtb-bg text-cfhtb-text rounded-md px-4 py-2.5 text-sm font-medium border border-cfhtb-light/40">
                    <Eye className="h-4 w-4" /> Consultation à l'écran uniquement
                  </span>
                )}
              </div>
              {doc.description && <p className="text-sm text-cfhtb-text mt-4 leading-relaxed">{doc.description}</p>}
              {doc.tags?.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <Tag className="h-3.5 w-3.5 text-cfhtb-light" />
                  {doc.tags.map((t) => (
                    <span key={t} className="text-xs bg-slate-100 text-cfhtb-text px-2 py-0.5 rounded-full">{t}</span>
                  ))}
                </div>
              )}
              {downloadable && (
                <p className="text-xs text-cfhtb-text/70 mt-4">
                  Le lien de téléchargement est signé et expire après 5 minutes.{" "}
                  <button onClick={load} data-testid="doc-refresh-link" className="inline-flex items-center gap-1 text-cfhtb-secondary hover:text-cfhtb-primary font-medium">
                    <RefreshCw className="h-3 w-3" /> Régénérer
                  </button>
                </p>
              )}
            </div>

            {isPdf ? (
              previewError ? (
                <div className="p-10 text-center text-sm text-cfhtb-text" data-testid="doc-preview-unavailable">
                  <FileText className="h-10 w-10 mx-auto text-cfhtb-light mb-3" />
                  L'aperçu n'est pas disponible pour ce document (fichier introuvable ou lien expiré).
                  <button onClick={load} className="ml-1 inline-flex items-center gap-1 text-cfhtb-secondary hover:text-cfhtb-primary font-medium">
                    <RefreshCw className="h-3 w-3" /> Réessayer
                  </button>
                </div>
              ) : !previewUrl ? (
                <div className="p-10 flex justify-center" data-testid="doc-preview-loading">
                  <div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <iframe
                  src={previewUrl}
                  title={`Aperçu — ${doc.titre}`}
                  data-testid="doc-pdf-preview"
                  className="w-full h-[70vh] bg-slate-50"
                />
              )
            ) : (
              <div className="p-10 text-center text-sm text-cfhtb-text" data-testid="doc-no-preview">
                <FileText className="h-10 w-10 mx-auto text-cfhtb-light mb-3" />
                {downloadable
                  ? "L'aperçu intégré est disponible pour les PDF. Utilisez le bouton Télécharger pour ce format."
                  : "L'aperçu intégré est disponible pour les PDF uniquement."}
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
